// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { spacing } from "@recidiviz/design-system";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import styled from "styled-components";

import { useFeatureVariants } from "../../components/StoreProvider";
import useIsMobile from "../../hooks/useIsMobile";
import { CaseloadTasksPresenterV2 } from "../../WorkflowsStore/presenters/CaseloadTasksPresenterV2";
import { TableViewToggle } from "../OpportunityCaseloadView/TableViewToggle";
import { MaxWidthWithSidebar } from "../sharedComponents";
import WorkflowsCaseloadTabs from "../WorkflowsCaseloadControlBar";
import { WorkflowsFilterDropdown } from "../WorkflowsFilters/WorkflowsFilterDropdown";
import { WorkflowsUnderstaffedPill } from "../WorkflowsUnderstaffed";
import { SupervisionTaskCategory, TASK_SELECTOR_LABELS } from "./fixtures";
import { TasksHeader } from "./styles";
import { TaskPreviewModal } from "./TaskPreviewModal";
import { TasksDescription } from "./TasksDescription";
import { TasksList } from "./TasksList";
import { TasksTable } from "./TasksTable";

// TODO(#7571): Add Noir/Cool Grey to design system
const TasksTabUnderline = styled.div`
  border-bottom: #00113326 1px solid;
`;

const TasksTopbarContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  ${({ $isMobile }) =>
    $isMobile
      ? `flex-direction: column;`
      : `flex-direction: row;
         gap: ${rem(spacing.sm)};
    `};
  justify-content: space-between;
  ${MaxWidthWithSidebar}
`;

const TableControls = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  gap: ${({ $isMobile }) => ($isMobile ? rem(spacing.lg) : rem(spacing.md))};
  // Keep the controls right-aligned in the top bar even when the left-hand item
  // (route planner link / description) is absent.
  margin-left: auto;
`;

export type WorkflowsTasksBodyV2Props = {
  presenter: CaseloadTasksPresenterV2;
};

export const WorkflowsTasksBodyV2 = observer(function WorkflowsTasksBodyV2({
  presenter,
}: WorkflowsTasksBodyV2Props) {
  const { isMobile } = useIsMobile(true);
  const { typesenseCaseloadSearch } = useFeatureVariants();

  const tabBadges = {
    ALL_TASKS: presenter.countForCategory("ALL_TASKS"),
    OVERDUE: presenter.countForCategory("OVERDUE"),
    DUE_THIS_WEEK: presenter.countForCategory("DUE_THIS_WEEK"),
    DUE_THIS_MONTH: presenter.countForCategory("DUE_THIS_MONTH"),
    DUE_NEXT_MONTH: presenter.countForCategory("DUE_NEXT_MONTH"),
    DUE_LATER: presenter.countForCategory("DUE_LATER"),
    HIDDEN: presenter.countForCategory("HIDDEN"),
  } as Partial<Record<SupervisionTaskCategory, number>>;

  const setActiveTab = (tab: SupervisionTaskCategory) => {
    runInAction(() => {
      presenter.selectedTaskCategory = tab;
    });
  };

  return (
    <>
      {!typesenseCaseloadSearch && (
        <>
          <TasksHeader>
            Tasks
            <WorkflowsUnderstaffedPill />
          </TasksHeader>
          <TasksTopbarContainer $isMobile={isMobile}>
            <TasksDescription
              showRoutePlannerLink={presenter.showRoutePlannerLink}
            >
              {presenter.pageDescriptionMarkdown}
            </TasksDescription>
            <TableControls $isMobile={isMobile}>
              <TableViewToggle presenter={presenter} />
              <WorkflowsFilterDropdown presenter={presenter} />
            </TableControls>
          </TasksTopbarContainer>
        </>
      )}

      {presenter.showListView ? (
        <TasksList presenter={presenter} />
      ) : (
        <>
          <TasksTabUnderline>
            <WorkflowsCaseloadTabs<SupervisionTaskCategory>
              tabs={presenter.displayedTaskCategories}
              tabLabels={TASK_SELECTOR_LABELS}
              tabBadges={tabBadges}
              activeTab={presenter.selectedTaskCategory}
              setActiveTab={setActiveTab}
            />
          </TasksTabUnderline>
          <TasksTable presenter={presenter} />
        </>
      )}
      <TaskPreviewModal presenter={presenter} />
    </>
  );
});
